/**
 * Plant Overview Component
 * Displays site status with SCADA data overlay
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Chip,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  Visibility as VisibilityIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  Navigation as NavigationIcon,
  TableChart as TableIcon,
  ViewModule as GridIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  TimeScale
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import { sitesService, SiteDashboardData, CurrentValuesDisplayConfig } from '../../services/sitesService';
import { TagDisplay } from './TagDisplay';
import { scadaUtils } from '../../services/scadaService';
import { useSiteContext } from '../../hooks/useSiteContext';
import { useAuth, useRoleCheck } from '../../hooks/useAuth';
// Import new enhanced components
import { EnhancedSCADATable } from './EnhancedSCADATable';
import { MultiBooleanBadge, VALVE_STATES, useMultiBooleanState } from './MultiBooleanBadge';
import { MultiBoolHistoryModal } from './MultiBoolHistoryModal';
import { ResponsiveImageContainer } from './ResponsiveImageContainer';
import { GraphicsOverlayLayer } from './GraphicsOverlayLayer';
// Import SCADA write components
import { EnableControlButton } from './EnableControlButton';
import { SetpointWriteDialog } from './SetpointWriteDialog';
// Import status banner component
import { PlantStatusBanner } from './PlantStatusBanner';
import { UIConfigurations, BooleanWriteButtonConfig, PositionedElement } from '../../types/banners';
// Import boolean write button components
import { BooleanWriteButton } from './BooleanWriteButton';
import { booleanWriteService } from '../../services/booleanWriteService';
import { scadaWriteAPI } from '../../services/scadaWriteService';
// Import table badge component
import ConfigurableTableBadge from './ConfigurableTableBadge';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Legend,
  TimeScale
);



interface PlantOverviewProps {
  siteId: number;
}

type ViewMode = 'image' | 'table' | 'grid';

export const PlantOverview: React.FC<PlantOverviewProps> = ({ siteId }) => {
  const { currentSite } = useSiteContext();
  const { user } = useAuth();
  const { canWriteSCADATags } = useRoleCheck();
  
  // User permissions for write functionality
  const userPermissions = {
    can_write_scada_tags: canWriteSCADATags(),
    role: user?.role || 'unknown'
  };
  const [dashboard, setDashboard] = useState<SiteDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [writeSessionActive, setWriteSessionActive] = useState(false);
  const [selectedTag, setSelectedTag] = useState<any | null>(null);
  const [showWriteDialog, setShowWriteDialog] = useState(false);
  const [historicalData, setHistoricalData] = useState<any>(null);
  const [showHistoricalModal, setShowHistoricalModal] = useState(false);
  const [historyHours, setHistoryHours] = useState<number>(24);
  const [currentAreaIndex, setCurrentAreaIndex] = useState(0);
  const [currentArea, setCurrentArea] = useState<{
    id: number;
    area_name: string;
    image: string | null;
    width: number;
    height: number;
    is_active: boolean;
  } | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('image');
  
  // Overlay interaction state
  const [overlayModal, setOverlayModal] = useState<{
    open: boolean;
    overlay: any | null;
    actionType: string;
    content: any;
  }>({
    open: false,
    overlay: null,
    actionType: '',
    content: null
  });

  // MultiBool badge history modal state
  const [multiBoolModal, setMultiBoolModal] = useState<{
    open: boolean;
    badgeId: number | null;
    badgeName: string;
  }>({
    open: false,
    badgeId: null,
    badgeName: '',
  });
  
  // UI Enhancement configurations
  const [uiConfigurations, setUIConfigurations] = useState<UIConfigurations>({
    badges: [],
    overlays: [],
    custom_displays: [],
    status_banners: [],
    site_id: 0
  });
  
  // Positioned elements from unified admin system
  const [positionedElements, setPositionedElements] = useState<{
    area_id: number | null;
    elements: any[];
  }>({
    area_id: null,
    elements: []
  });
  const [currentValuesConfig, setCurrentValuesConfig] = useState<CurrentValuesDisplayConfig>({
    current_values_box: {
      show: true,
      display_mode: 'large_buttons'
    },
    plant_image_overlays: {
      show: true,
      display_mode: 'status_icon',
      show_tooltips: true,
      tooltip_content: 'name_value'
    },
    alarm_indicators: {
      show: true,
      style: 'icon'
    }
  });

  // Fetch current values configuration
  useEffect(() => {
    const fetchCurrentValuesConfig = async () => {
      try {
        const config = await sitesService.getCurrentValuesConfig(siteId);
        setCurrentValuesConfig(config.current_values_display_config);
      } catch (error) {
        console.error('Failed to fetch current values config:', error);
        // Use default configuration
        setCurrentValuesConfig({
          current_values_box: {
            show: true,
            display_mode: 'large_buttons'
          },
          plant_image_overlays: {
            show: true,
            display_mode: 'status_icon',
            show_tooltips: true,
            tooltip_content: 'name_value'
          },
          alarm_indicators: {
            show: true,
            style: 'icon'
          }
        });
      }
    };

    if (siteId) {
      fetchCurrentValuesConfig();
    }
  }, [siteId, currentArea]);

  const fetchUIConfigurations = useCallback(async () => {
    if (!siteId) return;
    
    try {
      console.log('Fetching UI configurations for site:', siteId);
      const response = await fetch(`/api/v1/sites/ui-configurations/?site_id=${siteId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });
      
      console.log('UI configurations response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('UI configurations data:', data);
        console.log('Number of badges found:', data.badges?.length || 0);
        setUIConfigurations(data);
      } else {
        console.error('UI configurations request failed:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Failed to fetch UI configurations:', error);
    }
  }, [siteId]);

  const fetchPositionedElements = useCallback(async () => {
    if (!currentArea?.id) return;
    
    try {
      console.log('Fetching positioned elements for area:', currentArea.id);
      const response = await fetch(`/api/v1/sites/positioned-elements/${currentArea.id}/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });
      
      console.log('Positioned elements response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Positioned elements data:', data);
        console.log('Number of elements found:', data.elements?.length || 0);
        setPositionedElements({
          area_id: data.area_id,
          elements: data.elements || []
        });
      } else {
        console.error('Positioned elements request failed:', response.status, response.statusText);
        // Fallback to empty elements if API fails
        setPositionedElements({
          area_id: currentArea.id,
          elements: []
        });
      }
    } catch (error) {
      console.error('Failed to fetch positioned elements:', error);
      // Fallback to empty elements if API fails
      setPositionedElements({
        area_id: currentArea?.id || null,
        elements: []
      });
    }
  }, [currentArea?.id]);

  // Check write session status on mount and refresh
  const checkWriteSessionStatus = useCallback(async () => {
    try {
      const status = await scadaWriteAPI.getWriteSessionStatus(siteId);
      const isActive = status.session_active && (status.remaining_seconds === null || status.remaining_seconds > 0);
      setWriteSessionActive(isActive);
      console.log('Write session status checked:', { session_active: status.session_active, remaining_seconds: status.remaining_seconds, isActive });
    } catch (err) {
      console.error('Failed to check write session status on mount:', err);
      setWriteSessionActive(false);
    }
  }, [siteId]);

  const fetchDashboardData = useCallback(async () => {
    if (!siteId) return;
    
    try {
      setLoading(true);
      setError(null);
      const data = await sitesService.getSiteDashboard(siteId);
      setDashboard(data);
      
      // Set current area
      if (data.available_areas && data.available_areas.length > 0) {
        setCurrentArea(data.available_areas[currentAreaIndex]);
      }
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [siteId, currentAreaIndex]);

  useEffect(() => {
    fetchDashboardData();
    fetchUIConfigurations();
    checkWriteSessionStatus();
  }, [fetchDashboardData, fetchUIConfigurations, checkWriteSessionStatus]);

  useEffect(() => {
    fetchPositionedElements();
  }, [fetchPositionedElements]);

  // Auto-refresh banner data every 30 seconds to keep status banners current
  useEffect(() => {
    const refreshInterval = setInterval(() => {
      console.log('🔄 Auto-refreshing plant overview banners...');
      fetchUIConfigurations();
      fetchDashboardData(); // Also refresh SCADA data that banners depend on
    }, 30000); // 30 seconds

    return () => clearInterval(refreshInterval);
  }, [fetchUIConfigurations, fetchDashboardData]);

  const handleRefresh = () => {
    fetchDashboardData();
    fetchUIConfigurations();
    fetchPositionedElements();
    checkWriteSessionStatus();
  };

  const handleBooleanWrite = async (buttonId: number, writeReason?: string) => {
    try {
      const result = await booleanWriteService.writeButton(buttonId, writeReason);
      
      if (result.success) {
        // Refresh data after successful write for instant UI updates
        console.log('✅ Boolean write successful, refreshing data...', result);
        fetchDashboardData();
        fetchUIConfigurations(); // Refresh banners as they may depend on tag values
        fetchPositionedElements();
      }
      
      return result;
    } catch (error) {
      console.error('Boolean write failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Write operation failed'
      };
    }
  };

  const handleGetUpdatedTagValue = useCallback(async (targetTagName: string): Promise<boolean> => {
    try {
      // Refresh the dashboard to get latest tag values from PLC
      await fetchDashboardData();
      
      // Find the tag in the current dashboard data
      if (dashboard?.tags) {
        const tag = dashboard.tags.find(t => t.tag_name === targetTagName);
        if (tag) {
          const value = Boolean(tag.current_value);
          console.log(`🔍 Verification: Tag ${targetTagName} current value = ${value}`);
          return value;
        }
      }
      
      // Fallback: return false if tag not found
      console.warn(`⚠️ Could not find tag ${targetTagName} in dashboard data for verification`);
      return false;
    } catch (error) {
      console.error(`❌ Failed to get updated value for tag ${targetTagName}:`, error);
      throw error;
    }
  }, [dashboard, fetchDashboardData]);

  const handleAreaChange = (event: React.SyntheticEvent, newIndex: number) => {
    setCurrentAreaIndex(newIndex);
    
    // Check if this is the Alarms tab (last tab)
    const totalAreas = dashboard?.available_areas?.length || 0;
    if (newIndex >= totalAreas) {
      // This is the Alarms tab - don't set currentArea
      setCurrentArea(null);
    } else {
      // This is a plant area tab
      if (dashboard?.available_areas) {
      setCurrentArea(dashboard.available_areas[newIndex]);
      }
    }
  };
  
  const handleTagClick = (tag: any) => {
    setSelectedTag(tag.id);
    fetchTagHistory(tag.id);
  };

  const handleOverlayClick = async (overlay: any) => {
    console.log('🎯 Overlay clicked:', overlay);
    
    switch (overlay.click_action) {
      case 'table':
        if (overlay.target_table_badge) {
          try {
            // Fetch the table badge configuration
            const response = await fetch(`/api/v1/sites/ui-configurations/table-badges/${overlay.target_table_badge}/`);
            if (response.ok) {
              const tableBadge = await response.json();
              setOverlayModal({
                open: true,
                overlay,
                actionType: 'table',
                content: tableBadge
              });
            } else {
              console.error('Failed to fetch table badge configuration');
            }
          } catch (error) {
            console.error('Error fetching table badge:', error);
          }
        }
        break;
      
      case 'custom_url':
        if (overlay.custom_url) {
          window.open(overlay.custom_url, '_blank', 'noopener,noreferrer');
        }
        break;
      
      case 'popup_info':
        setOverlayModal({
          open: true,
          overlay,
          actionType: 'popup_info',
          content: overlay.click_config || { title: overlay.name, message: overlay.description }
        });
        break;
      
      case 'graph':
        // For now, show tag history for the trigger tag
        const triggerTag = getCurrentAreaTags().find(tag => tag.tag_name === overlay.trigger_tag_name);
        if (triggerTag) {
          setSelectedTag(triggerTag.id);
          fetchTagHistory(triggerTag.id);
        }
        break;
      
      default:
        console.log('No action configured for overlay');
    }
  };
  
  const fetchTagHistory = async (tagId: number, hours: number = historyHours) => {
    try {
      const selected = dashboard?.tags.find(t => t.id === tagId);
      const end = new Date();
      const start = new Date(end.getTime() - hours * 60 * 60 * 1000);

      // If we can infer BOOL, use step series endpoint for proper step chart
      if ((selected as any)?.data_type === 'BOOL' || /^(on|off|true|false)$/i.test(selected?.current_value_display || '')) {
        const params = new URLSearchParams({
          tag_id: String(tagId),
          start: start.toISOString(),
          end: end.toISOString()
        }).toString();
        const resp = await fetch(`/api/v1/sites/scada/boolean-series/?${params}`);
        if (resp.ok) {
          const payload = await resp.json();
          const points = (payload.points || []) as Array<{ t: string; v: number }>;
          const data_points = points.map(p => ({ timestamp: p.t, value: p.v, quality: 'GOOD' }));
          setHistoricalData({ data_points });
          setHistoryHours(hours);
          setShowHistoricalModal(true);
          return;
        }
      }

      // Fallback to numeric historian
      const response = await fetch(`/api/v1/sites/scada/tags/${tagId}/historical_data/?hours=${hours}`);
      if (response.ok) {
        const data = await response.json();
        setHistoricalData(data);
        setHistoryHours(hours);
        setShowHistoricalModal(true);
      } else {
        console.error('Failed to fetch tag history:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Failed to fetch tag history:', error);
    }
  };

  const getTagStatusIcon = (status: string) => {
    switch (status) {
      case 'NORMAL':
      case 'ALARMS_DISABLED':
        return <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />;
      case 'HIGH_ALARM':
      case 'LOW_ALARM':
      case 'BOOLEAN_TRUE_ALARM':
      case 'BOOLEAN_FALSE_ALARM':
        return <ErrorIcon sx={{ color: 'error.main', fontSize: 20 }} />;
      default:
        return <InfoIcon sx={{ color: 'info.main', fontSize: 20 }} />;
    }
  };

  const getCurrentAreaTags = () => {
    // Use positioned elements if available, otherwise fallback to legacy dashboard data
    if (positionedElements.area_id === currentArea?.id && positionedElements.elements.length > 0) {
      console.log('Using unified positioned elements:', positionedElements.elements.length);
      // Filter for SCADA tags only and add compatibility fields
      return positionedElements.elements
        .filter(element => element.type === 'scada_tag')
        .map(element => ({
          id: element.id,
          tag_name: element.name,
          description: element.description,
          current_value: element.current_value,
          current_value_display: element.current_value_display,
          alarm_status: element.alarm_status,
          engineering_unit: element.engineering_unit,
          display_mode: element.display_mode,
          // Position data - convert pixels to percentages for frontend display
          overview_position_x: (element.position_x / (currentArea?.width || 800)) * 100,
          overview_position_y: (element.position_y / (currentArea?.height || 600)) * 100,
          // Keep area reference for compatibility
          area_id: currentArea?.id,
          // Add other fields for compatibility
          equipment_name: element.description || 'Equipment',
          timestamp: new Date().toISOString(),
          alarms_enabled: true,
          z_index: element.z_index,
          is_visible: element.is_visible,
          // Write operation fields - CRITICAL for write functionality
          is_writable: element.is_writable || false,
          write_available: element.write_available || false,
          control_switch_status: element.control_switch_status || 'unknown',
          write_disabled_reason: element.write_disabled_reason || null,
          write_min_value: element.write_min_value || null,
          write_max_value: element.write_max_value || null,
          site: element.site || siteId
        }));
    }
    
    // Fallback to legacy dashboard data
    if (!dashboard?.tags) return [];
    console.log('Using legacy dashboard data');
    
    return dashboard.tags.filter(tag => {
      const hasValidPosition = tag.overview_position_x !== null && tag.overview_position_y !== null;
      const belongsToCurrentArea = currentArea && tag.area_id === currentArea.id;
      return hasValidPosition && belongsToCurrentArea;
    });
  };

  const getAllAlarms = () => {
    if (!dashboard?.tags) return [];
    
    return dashboard.tags.filter(tag => 
      tag.alarms_enabled || 
      ['HIGH_ALARM', 'LOW_ALARM', 'BOOLEAN_TRUE_ALARM', 'BOOLEAN_FALSE_ALARM'].includes(tag.alarm_status)
    );
  };

  const handleViewModeChange = (event: React.MouseEvent<HTMLElement>, newMode: ViewMode | null) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!dashboard) {
    return (
      <Alert severity="info">
        No dashboard data available
          </Alert>
    );
  }

  return (
    <Box>
      {/* Plant Status Overview with Tabs */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          {/* Area Navigation Tabs - including Alarms as the last tab */}
          {dashboard.available_areas && dashboard.available_areas.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Tabs 
                value={currentAreaIndex} 
                onChange={handleAreaChange}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ borderBottom: 1, borderColor: 'divider' }}
              >
                {dashboard.available_areas.map((area, index) => (
                  <Tab 
                    key={area.id} 
                    label={area.area_name}
                    icon={<NavigationIcon />}
                    iconPosition="start"
                  />
                ))}
                {/* Add Alarms tab at the end */}
                <Tab 
                  label="Alarms"
                  icon={<WarningIcon />}
                  iconPosition="start"
                />
              </Tabs>
              
              {/* Controls aligned with tabs */}
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
                {/* SCADA Write Control Button */}
                {canWriteSCADATags() && siteId && (
                  <EnableControlButton
                    siteId={siteId}
                    siteName={currentSite?.siteName || 'Site'}
                    onSessionStart={() => setWriteSessionActive(true)}
                    onSessionEnd={() => setWriteSessionActive(false)}
                    userPermissions={{
                      can_write_scada_tags: canWriteSCADATags(),
                      role: user?.role || 'operator'
                    }}
                    sessionDurationMinutes={10}
                    size="small"
                    variant="outlined"
                  />
                )}
                
                {/* View Mode Toggle - only show when not on alarms tab */}
                {currentAreaIndex < (dashboard.available_areas?.length || 0) && (
                  <ToggleButtonGroup
                    value={viewMode}
                    exclusive
                    onChange={handleViewModeChange}
                    size="small"
                  >
                    <ToggleButton value="image">
                      <Tooltip title="Image View">
                        <NavigationIcon />
                      </Tooltip>
                    </ToggleButton>
                    <ToggleButton value="table">
                      <Tooltip title="Table View">
                        <TableIcon />
                      </Tooltip>
                    </ToggleButton>
                    <ToggleButton value="grid">
                      <Tooltip title="Grid View">
                        <GridIcon />
                      </Tooltip>
                    </ToggleButton>
                  </ToggleButtonGroup>
                )}
                
                <Button 
                  variant="outlined" 
                  size="small"
                  onClick={handleRefresh}
                  startIcon={<RefreshIcon />}
                >
                  Refresh
                </Button>
              </Box>
            </Box>
          )}
          
          {/* Plant Overview Content */}
          {currentAreaIndex < (dashboard.available_areas?.length || 0) ? (
            // Show plant area content based on view mode
            <>
              {viewMode === 'table' && (
                <EnhancedSCADATable
                  tags={getCurrentAreaTags().map(tag => ({
                    ...tag,
                    equipment_name: 'Equipment Name', // Would come from API
                    timestamp: new Date().toISOString(),
                    alarm_priority: {
                      level: 2,
                      name: 'Monitor',
                      color: '#FFA500',
                      description: 'Monitor level alarm',
                    }
                  }))}
                  loading={loading}
                  onTagClick={handleTagClick}
                  onRefresh={handleRefresh}
                  showConfiguration={true}
                />
              )}
              
              {viewMode === 'image' && (
                <Box>
                  {/* Status Banners */}
                  <PlantStatusBanner 
                    banners={uiConfigurations.status_banners || []}
                    maxHeight={120}
                  />
                  
                  <ResponsiveImageContainer
                  src={currentArea?.image || ''}
                  alt={`${currentArea?.area_name} Overview`}
                  originalWidth={currentArea?.width || 800}
                  originalHeight={currentArea?.height || 600}
                  maxHeight="60vh"
                >
                  {/* Graphics Overlays Layer */}
                  <GraphicsOverlayLayer
                    overlays={uiConfigurations.overlays || []}
                    positionedElements={positionedElements.elements || []}
                    tags={getCurrentAreaTags()}
                    imageWidth={currentArea?.width || 800}
                    imageHeight={currentArea?.height || 600}
                    containerWidth={800} // This will be updated by ResponsiveImageContainer
                    containerHeight={600} // This will be updated by ResponsiveImageContainer
                    onOverlayClick={handleOverlayClick}
                  />

                  {/* Tag Display Overlays */}
                  {currentValuesConfig.plant_image_overlays.show && getCurrentAreaTags().map((tag) => (
                    <Box
                      key={tag.id}
                      sx={{
                        position: 'absolute',
                        left: `${tag.overview_position_x!}%`,
                        top: `${tag.overview_position_y!}%`,
                        transform: 'translate(-50%, -50%)',
                        zIndex: 10,
                        pointerEvents: 'auto',
                        cursor: 'pointer',
                        '&:hover .write-icon': {
                          opacity: tag.is_writable && writeSessionActive && userPermissions.can_write_scada_tags ? 1 : 0
                        }
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        // Check if this is a writable tag and we have an active session
                        if (tag.is_writable && writeSessionActive && userPermissions.can_write_scada_tags) {
                          // Open write dialog
                          setSelectedTag(tag);
                          setShowWriteDialog(true);
                        } else {
                          handleTagClick(tag);
                        }
                      }}
                    >
                      <Box sx={{ position: 'relative' }}>
                        <TagDisplay
                          tag={tag}
                          displayMode={(tag.display_mode as any) || currentValuesConfig.plant_image_overlays.display_mode}
                          showTooltips={currentValuesConfig.plant_image_overlays.show_tooltips}
                          tooltipContent={currentValuesConfig.plant_image_overlays.tooltip_content}
                          size="medium"
                        />
                        {/* Write icon overlay - only shows on hover for writable tags */}
                        {tag.is_writable && writeSessionActive && userPermissions.can_write_scada_tags && (
                          <Box
                            className="write-icon"
                            sx={{
                              position: 'absolute',
                              top: -4,
                              right: -4,
                              bgcolor: 'primary.main',
                              borderRadius: '50%',
                              width: 16,
                              height: 16,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              opacity: 0,
                              transition: 'opacity 0.2s',
                              zIndex: 1000
                            }}
                          >
                            <EditIcon sx={{ fontSize: 10, color: 'white' }} />
                          </Box>
                        )}
                      </Box>
                    </Box>
                  ))}

                  {/* Multi-Boolean Badge Overlays - Using Unified Positioning System */}
                  {(() => {
                    // Use positioned elements if available, otherwise fallback to legacy UI configurations
                    if (positionedElements.area_id === currentArea?.id && positionedElements.elements.length > 0) {
                      console.log('Rendering badges from unified system. Total elements:', positionedElements.elements.length);
                      const badgeElements = positionedElements.elements.filter(element => element.type === 'badge');
                      console.log('Badge elements found:', badgeElements.length);
                      
                      return badgeElements.map((element) => (
                        <Box
                          key={`unified-badge-${element.id}`}
                          sx={{
                            position: 'absolute',
                            left: `${element.position_x}%`,
                            top: `${element.position_y}%`,
                            transform: 'translate(-50%, -50%)',
                            zIndex: element.z_index || 15,
                            pointerEvents: 'auto',
                            cursor: 'pointer',
                            opacity: element.is_visible ? 1 : 0.5,
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            console.log('Unified badge clicked:', element.name);
                            console.log('Badge element data:', {
                              id: element.id,
                              object_id: element.object_id,
                              type: element.content_type,
                              name: element.name
                            });
                            if (element.object_id) {
                              console.log('Opening MultiBool modal for badge ID:', element.object_id);
                              setMultiBoolModal({
                                open: true,
                                badgeId: element.object_id,
                                badgeName: element.name || 'MultiBool Badge',
                              });
                            } else {
                              console.error('No object_id found for badge:', element);
                            }
                          }}
                        >
                          <MultiBooleanBadge
                            title={element.name}
                            // Create mock inputs from pattern to display the correct state
                            inputs={element.badge_state?.pattern ? 
                              element.badge_state.pattern.split('').map((bit, index) => ({
                                name: `Input${index + 1}`,
                                value: bit === '1',
                                description: `Input ${index + 1}`
                              })) : []
                            }
                            // Create state definition that matches the current state
                            stateDefinitions={element.badge_state ? [{
                              name: element.badge_state.name,
                              pattern: element.badge_state.pattern,
                              color: element.badge_state.color,
                              description: element.badge_state.description || '',
                              priority: 1
                            }] : []}
                            showTooltip={true}
                            showLabel={element.show_label}
                            labelText={element.label_text}
                            kksCode={element.kks_code}
                          />
                        </Box>
                      ));
                    }
                    
                    // Legacy system disabled - badges now handled by unified positioning
                    return [];
                  })()}

                  {/* Boolean Write Buttons from Positioned Elements */}
                  {positionedElements.area_id === currentArea?.id && 
                    positionedElements.elements
                      ?.filter((element: PositionedElement) => 
                        element.type === 'boolean_write_button' && element.is_visible
                      )
                      .map((element: PositionedElement) => (
                        <Box
                          key={`button-${element.id}`}
                          sx={{
                            position: 'absolute',
                            left: `${(element.position_x / (currentArea?.width || 1)) * 100}%`,
                            top: `${(element.position_y / (currentArea?.height || 1)) * 100}%`,
                            transform: 'translate(-50%, -50%)',
                            zIndex: element.z_index,
                            pointerEvents: 'auto',
                          }}
                        >
                          <BooleanWriteButton
                            config={element.element as BooleanWriteButtonConfig}
                            writeEnabled={writeSessionActive}
                            hasWritePermission={userPermissions.can_write_scada_tags}
                            onWrite={handleBooleanWrite}
                            onRefresh={handleRefresh}
                            onGetUpdatedValue={() => handleGetUpdatedTagValue(element.element?.target_tag_name || '')}
                          />
                        </Box>
                      ))
                  }

                  {/* Table Badges from Positioned Elements */}
                  {positionedElements.area_id === currentArea?.id && 
                    positionedElements.elements
                      ?.filter((element: PositionedElement) => 
                        element.type === 'table_badge' && element.is_visible
                      )
                      .map((element: PositionedElement) => (
                        <Box
                          key={`table-badge-${element.id}`}
                          sx={{
                            position: 'absolute',
                            left: `${(element.position_x / (currentArea?.width || 1)) * 100}%`,
                            top: `${(element.position_y / (currentArea?.height || 1)) * 100}%`,
                            transform: 'translate(-50%, -50%)',
                            zIndex: element.z_index,
                            pointerEvents: 'auto',
                            maxWidth: '300px', // Limit table width
                          }}
                        >
                          <ConfigurableTableBadge
                            title={element.name}
                            tags={getCurrentAreaTags().map(tag => ({
                              id: tag.id,
                              tag_name: tag.tag_name,
                              description: tag.description,
                              current_value: tag.current_value,
                              unit: tag.engineering_unit,
                              quality: 'GOOD' as const, // Default to GOOD, could be enhanced later
                              alarm_definitions: [], // Could be populated from tag data if available
                              last_updated: new Date().toISOString()
                            }))}
                            columns={element.element?.columns?.length > 0 ? element.element.columns : [
                              // Default columns if none configured
                              { id: 'tag_name', label: 'Tag Name', type: 'tag_name', visible: true },
                              { id: 'value', label: 'Value', type: 'value', visible: true },
                              { id: 'unit', label: 'Unit', type: 'unit', visible: true },
                              { id: 'alarm_status', label: 'Status', type: 'alarm_status', visible: true }
                            ]}
                            maxHeight={200}
                            collapsible={true}
                            defaultExpanded={true}
                            size="small"
                            onTagClick={(tableTag) => {
                              // Find the original SCADA tag and call handleTagClick
                              const originalTag = getCurrentAreaTags().find(t => t.id === tableTag.id);
                              if (originalTag) handleTagClick(originalTag);
                            }}
                          />
                        </Box>
                      ))
                  }

                </ResponsiveImageContainer>
                </Box>
              )}
              
              {viewMode === 'grid' && (
                <Grid container spacing={2}>
                  {getCurrentAreaTags().map((tag) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={tag.id}>
                      <Card 
                        variant="outlined" 
                        sx={{ 
                          cursor: 'pointer',
                          borderColor: ['HIGH_ALARM', 'LOW_ALARM', 'BOOLEAN_TRUE_ALARM', 'BOOLEAN_FALSE_ALARM'].includes(tag.alarm_status) 
                            ? 'error.main' 
                            : 'divider',
                          bgcolor: ['HIGH_ALARM', 'LOW_ALARM', 'BOOLEAN_TRUE_ALARM', 'BOOLEAN_FALSE_ALARM'].includes(tag.alarm_status) 
                            ? 'error.light' 
                            : 'background.paper'
                        }} 
                        onClick={() => handleTagClick(tag)}
                      >
                        <CardContent sx={{ p: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            {getTagStatusIcon(tag.alarm_status)}
                            <Typography variant="subtitle2" sx={{ ml: 1, flexGrow: 1 }}>
                              {tag.tag_name}
                            </Typography>
                          </Box>
                          <Typography variant="h6" color={
                            ['NORMAL', 'ALARMS_DISABLED'].includes(tag.alarm_status) ? 'success.main' : 
                            ['HIGH_ALARM', 'LOW_ALARM', 'BOOLEAN_TRUE_ALARM', 'BOOLEAN_FALSE_ALARM'].includes(tag.alarm_status) ? 'error.main' : 
                            'success.main'
                          }>
                            {tag.current_value_display}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {tag.description}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </>
          ) : (
            // Show Enhanced Alarms Table
            <EnhancedSCADATable
              tags={getAllAlarms().map(tag => ({
                ...tag,
                equipment_name: 'Equipment Name', // Would come from API
                timestamp: new Date().toISOString(),
                alarm_priority: {
                  level: ['HIGH_ALARM', 'LOW_ALARM', 'BOOLEAN_TRUE_ALARM', 'BOOLEAN_FALSE_ALARM'].includes(tag.alarm_status) ? 1 : 2,
                  name: ['HIGH_ALARM', 'LOW_ALARM', 'BOOLEAN_TRUE_ALARM', 'BOOLEAN_FALSE_ALARM'].includes(tag.alarm_status) ? 'Critical to Plant' : 'Monitor',
                  color: ['HIGH_ALARM', 'LOW_ALARM', 'BOOLEAN_TRUE_ALARM', 'BOOLEAN_FALSE_ALARM'].includes(tag.alarm_status) ? '#FF0000' : '#FFA500',
                  description: ['HIGH_ALARM', 'LOW_ALARM', 'BOOLEAN_TRUE_ALARM', 'BOOLEAN_FALSE_ALARM'].includes(tag.alarm_status) ? 'Critical alarm requiring immediate attention' : 'Monitor level alarm',
                }
              }))}
              loading={loading}
              onTagClick={handleTagClick}
              onRefresh={handleRefresh}
              showConfiguration={true}
            />
          )}
        </CardContent>
      </Card>

      {/* Current Values Section */}
      {currentAreaIndex < (dashboard.available_areas?.length || 0) && currentValuesConfig.current_values_box.show && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Current Values
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Real-time SCADA tag values {currentArea && `for ${currentArea.area_name}`}
            </Typography>
            
            {currentValuesConfig.current_values_box.display_mode === 'table' ? (
              // Table display mode
              <Box sx={{ mt: 2 }}>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Tag Name</TableCell>
                        <TableCell>Value</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Description</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {getCurrentAreaTags().map((tag) => (
                        <TableRow 
                          key={tag.id} 
                          hover 
                          sx={{ cursor: 'pointer' }}
                          onClick={() => handleTagClick(tag)}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              {getTagStatusIcon(tag.alarm_status)}
                              <Typography variant="body2" sx={{ ml: 1 }}>
                                {tag.tag_name}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography 
                              variant="body2" 
                              color={
                                ['NORMAL', 'ALARMS_DISABLED'].includes(tag.alarm_status) ? 'success.main' : 
                                ['HIGH_ALARM', 'LOW_ALARM', 'BOOLEAN_TRUE_ALARM', 'BOOLEAN_FALSE_ALARM'].includes(tag.alarm_status) ? 'error.main' : 
                                'success.main'
                              }
                              sx={{ fontWeight: 'bold' }}
                            >
                              {tag.current_value_display}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              size="small" 
                              label={tag.alarm_status.replace('_', ' ')}
                              color={
                                ['NORMAL', 'ALARMS_DISABLED'].includes(tag.alarm_status) ? 'success' : 
                                ['HIGH_ALARM', 'LOW_ALARM', 'BOOLEAN_TRUE_ALARM', 'BOOLEAN_FALSE_ALARM'].includes(tag.alarm_status) ? 'error' : 
                                'default'
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {tag.description}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ) : (
              // Button display modes (large or small)
              <Grid container spacing={currentValuesConfig.current_values_box.display_mode === 'large_buttons' ? 2 : 1} sx={{ mt: 1 }}>
                {getCurrentAreaTags().map((tag) => (
                  <Grid 
                    item 
                    xs={currentValuesConfig.current_values_box.display_mode === 'large_buttons' ? 12 : 6} 
                    sm={currentValuesConfig.current_values_box.display_mode === 'large_buttons' ? 6 : 4} 
                    md={currentValuesConfig.current_values_box.display_mode === 'large_buttons' ? 4 : 3} 
                    lg={currentValuesConfig.current_values_box.display_mode === 'large_buttons' ? 3 : 2} 
                    key={tag.id}
                  >
                    <Card 
                      variant="outlined" 
                      sx={{ 
                        cursor: 'pointer',
                        p: currentValuesConfig.current_values_box.display_mode === 'large_buttons' ? 2 : 1
                      }} 
                      onClick={() => handleTagClick(tag)}
                    >
                      <CardContent sx={{ p: currentValuesConfig.current_values_box.display_mode === 'large_buttons' ? 2 : 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          {getTagStatusIcon(tag.alarm_status)}
                          <Typography 
                            variant={currentValuesConfig.current_values_box.display_mode === 'large_buttons' ? 'subtitle2' : 'caption'} 
                            sx={{ ml: 1, flexGrow: 1 }}
                          >
                            {tag.tag_name}
                            {tag.link_to_area && (
                              <Chip 
                                size="small" 
                                label={`→ ${tag.link_to_area}`}
                                sx={{ ml: 1, fontSize: '10px', height: '16px' }}
                              />
                            )}
                          </Typography>
                        </Box>
                        <Typography 
                          variant={currentValuesConfig.current_values_box.display_mode === 'large_buttons' ? 'h6' : 'body2'} 
                          color={
                            ['NORMAL', 'ALARMS_DISABLED'].includes(tag.alarm_status) ? 'success.main' : 
                            ['HIGH_ALARM', 'LOW_ALARM', 'BOOLEAN_TRUE_ALARM', 'BOOLEAN_FALSE_ALARM'].includes(tag.alarm_status) ? 'error.main' : 
                            'success.main'
                          }
                        >
                          {tag.current_value_display}
                        </Typography>
                        {currentValuesConfig.current_values_box.display_mode === 'large_buttons' && (
                          <Typography variant="caption" color="text.secondary">
                            {tag.description}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </CardContent>
        </Card>
      )}

      {/* Historical Data Modal */}
      <Dialog 
        open={showHistoricalModal} 
        onClose={() => setShowHistoricalModal(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Historical Data - {dashboard?.tags.find(tag => tag.id === selectedTag)?.tag_name}
        </DialogTitle>
        <DialogContent>
          {historicalData ? (
                <Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} sx={{ flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="h6">
                      {historyHours} Hour Trend
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                      <InputLabel>Time Range</InputLabel>
                      <Select
                        value={historyHours}
                        label="Time Range"
                        onChange={(e) => {
                          const newInterval = Number(e.target.value);
                          if (selectedTag) {
                            setHistoryHours(newInterval);
                            fetchTagHistory(selectedTag, newInterval);
                          }
                        }}
                      >
                        <MenuItem value={1}>1 Hour</MenuItem>
                        <MenuItem value={6}>6 Hours</MenuItem>
                        <MenuItem value={12}>12 Hours</MenuItem>
                        <MenuItem value={24}>24 Hours</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  <Box sx={{ height: 400, width: '100%' }}>
                    <Line
                      data={{
                        datasets: [{
                          label: dashboard?.tags.find(tag => tag.id === selectedTag)?.tag_name || 'Value',
                          data: historicalData.data_points
                            .sort((a: any, b: any) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
                            .map((point: any) => ({
                              x: new Date(point.timestamp).getTime(),
                              y: point.value
                            })),
                          borderColor: scadaUtils.getAlarmStatusColor(dashboard?.tags.find(tag => tag.id === selectedTag)?.alarm_status || 'NORMAL'),
                          backgroundColor: scadaUtils.getAlarmStatusColor(dashboard?.tags.find(tag => tag.id === selectedTag)?.alarm_status || 'NORMAL') + '20',
                          borderWidth: 2,
                          pointRadius: 0,
                          pointHoverRadius: 4,
                          tension: 0,
                          stepped: (dashboard?.tags.find(tag => tag.id === selectedTag) as any)?.data_type === 'BOOL' ? 'before' : false
                        }]
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            position: 'top' as const,
                          },
                          title: {
                            display: true,
                            text: `${dashboard?.tags.find(tag => tag.id === selectedTag)?.tag_name} - Last ${historyHours} Hours`
                          }
                        },
                        scales: {
                          x: {
                            type: 'time',
                            time: {
                              displayFormats: {
                                hour: 'MMM dd HH:mm',
                                day: 'MMM dd',
                                minute: 'HH:mm'
                              },
                              tooltipFormat: 'MMM dd, yyyy HH:mm:ss'
                            },
                            title: {
                              display: true,
                              text: 'Time'
                            }
                          },
                          y: {
                            title: {
                              display: true,
                              text: dashboard?.tags.find(tag => tag.id === selectedTag)?.engineering_unit || 'Value'
                            }
                          }
                        }
                      }}
                    />
                  </Box>
                </Box>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowHistoricalModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Overlay Interaction Modal */}
      <Dialog
        open={overlayModal.open}
        onClose={() => setOverlayModal({ open: false, overlay: null, actionType: '', content: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {overlayModal.overlay?.name || 'Overlay Action'}
        </DialogTitle>
        <DialogContent>
          {overlayModal.actionType === 'table' && overlayModal.content && (
            <Box>
              <Typography variant="body1" gutterBottom>
                {overlayModal.content.description}
              </Typography>
              {/* TODO: Render enhanced table component here */}
              <Typography variant="body2" color="text.secondary">
                Table configuration loaded: {overlayModal.content.name}
              </Typography>
            </Box>
          )}
          
          {overlayModal.actionType === 'popup_info' && overlayModal.content && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {overlayModal.content.title || overlayModal.overlay?.name}
              </Typography>
              <Typography variant="body1">
                {overlayModal.content.message || overlayModal.overlay?.description}
              </Typography>
              {overlayModal.content.details && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {overlayModal.content.details}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOverlayModal({ open: false, overlay: null, actionType: '', content: null })}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* MultiBool Badge History Modal */}
      <MultiBoolHistoryModal
        open={multiBoolModal.open}
        onClose={() => setMultiBoolModal({ open: false, badgeId: null, badgeName: '' })}
        badgeId={multiBoolModal.badgeId || 0}
        badgeName={multiBoolModal.badgeName}
      />


      {/* SCADA Write Dialog */}
      <SetpointWriteDialog
        open={showWriteDialog}
        onClose={() => {
          setShowWriteDialog(false);
          setSelectedTag(null);
        }}
        tag={selectedTag}
        onWriteSuccess={(response) => {
          // Refresh all data after successful write for instant updates
          console.log('✅ SCADA write successful, refreshing data...', response);
          fetchDashboardData();
          fetchUIConfigurations(); // Refresh banners as they may depend on tag values
          fetchPositionedElements();
          setShowWriteDialog(false);
          setSelectedTag(null);
        }}
      />
    </Box>
  );
}; 